import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaBoxOpen, FaCalendarAlt, FaDownload, FaTimes, 
  FaReceipt, FaCreditCard, FaMapMarkerAlt, FaChevronRight 
} from 'react-icons/fa';
import Navbar from './Navbar';
import Fotter from './Fotter';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { BASE_URL } from '../backend/backend.js';

const UserOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Parse Backend Date string "DD-MM-YYYY HH:mm:ss" safely
  const parseBackendDate = (dateStr) => {
    if (!dateStr) return new Date();
    const [datePart, timePart] = dateStr.split(' ');
    const [day, month, year] = datePart.split('-');
    return new Date(`${year}-${month}-${day}T${timePart}`);
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await fetch(`${BASE_URL}/user/getUserOrder`, { credentials: 'include' });
        if (!response.ok) throw new Error(`Failed to fetch orders`);
        
        const data = await response.json();
        
        if (data?.body && Array.isArray(data.body)) {
          const formattedOrders = data.body.map(order => {
            const validDate = parseBackendDate(order.orderDate);
            return {
              id: order.id,
              orderId: `ORD-${validDate.toISOString().slice(0, 10).replace(/-/g, '')}-${order.id.toString().padStart(3, '0')}`,
              orderDate: validDate.toISOString(),
              totalAmount: order.totalPrice,
              paymentMode: order.paymentMode,
              orderStatus: order.orderStatus || 'Processing',
              fullAddress: order.fullAddress,
              subTotal: order.subTotal,
              discount: order.discount,
              otherCharge: order.otherCharge,
              orderItems: order.orderItems.map(item => ({
                id: item.id,
                quantity: item.quantity,
                price: item.price,
                medicine: { ...item.medicine },
              })),
            };
          });
          setOrders(formattedOrders.reverse()); // Newest first
        } else {
          setOrders([]);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  useEffect(() => {
    let results = [...orders];
    const now = new Date();

    if (filter === 'lastMonth') {
      const past = new Date(); past.setMonth(now.getMonth() - 1);
      results = results.filter(o => new Date(o.orderDate) >= past);
    } else if (filter === 'lastTwoMonths') {
      const past = new Date(); past.setMonth(now.getMonth() - 2);
      results = results.filter(o => new Date(o.orderDate) >= past);
    } else if (filter === 'dateRange' && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // Include whole end day
      results = results.filter(o => {
        const d = new Date(o.orderDate);
        return d >= start && d <= end;
      });
    }
    setFilteredOrders(results);
  }, [orders, filter, startDate, endDate]);

  const generateInvoice = (order, e) => {
    if (e) e.stopPropagation(); // Prevent opening modal if clicking download on card
    const doc = new jsPDF();
    
    // Modern PDF Design
    doc.setFillColor(248, 250, 252);
    doc.rect(0, 0, 210, 40, 'F');
    doc.setFontSize(22).setFont('helvetica', 'bold').setTextColor(30, 41, 59);
    doc.text('Girraj Medicare', 15, 25);
    doc.setFontSize(10).setFont('helvetica', 'normal').setTextColor(100, 116, 139);
    doc.text('Premium Digital Invoice', 15, 32);

    let y = 50;
    doc.setFontSize(12).setTextColor(30, 41, 59).setFont('helvetica', 'bold');
    doc.text('Order Information', 15, y);
    doc.setFont('helvetica', 'normal').setFontSize(10);
    doc.text(`Order ID: ${order.orderId}`, 15, y+=7);
    doc.text(`Date: ${new Date(order.orderDate).toLocaleString()}`, 15, y+=6);
    doc.text(`Payment: ${order.paymentMode}`, 15, y+=6);
    doc.text(`Address: ${order.fullAddress}`, 15, y+=6);

    const tableData = order.orderItems.map((item, i) => [
      i + 1,
      item.medicine.medicineName,
      item.quantity,
      `Rs ${(item.medicine.descountPrice || item.medicine.price).toFixed(2)}`,
      `Rs ${item.price.toFixed(2)}`,
    ]);

    doc.autoTable({
      startY: y + 10,
      head: [['#', 'Item', 'Qty', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255 },
      styles: { fontSize: 9, cellPadding: 5 },
    });

    const finalY = doc.autoTable.previous.finalY + 15;
    doc.setFontSize(10).setTextColor(100, 116, 139);
    doc.text(`Subtotal: Rs ${order.subTotal.toFixed(2)}`, 140, finalY);
    doc.text(`Discount: - Rs ${order.discount.toFixed(2)}`, 140, finalY + 6);
    doc.text(`Platform Fee: Rs ${order.otherCharge.toFixed(2)}`, 140, finalY + 12);
    
    doc.setFontSize(14).setFont('helvetica', 'bold').setTextColor(15, 23, 42);
    doc.text(`Grand Total: Rs ${order.totalAmount.toFixed(2)}`, 140, finalY + 22);

    doc.save(`${order.orderId}.pdf`);
  };

  const getStatusColor = (status) => {
    const s = status.toLowerCase();
    if (s.includes('deliver')) return '#10b981'; // Emerald
    if (s.includes('process') || s.includes('place')) return '#3b82f6'; // Blue
    if (s.includes('cancel')) return '#ef4444'; // Red
    return '#f59e0b'; // Amber
  };

  if (loading) return <div style={loadingScreen}><div style={spinner} /></div>;

  return (
    <div style={{ background: '#f4f7f8', minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>
      <Navbar />
      
      <main style={mainContainer}>
        {/* Header Section */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} style={headerSection}>
          <h1 style={title}>Order History</h1>
          <p style={subtitle}>Track, manage, and download invoices for your recent purchases.</p>
        </motion.div>

        {/* Next-Gen Pill Filter Bar */}
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={filterWrapper}>
          <div style={pillContainer}>
            {['all', 'lastMonth', 'lastTwoMonths'].map(f => (
              <button key={f} style={filter === f ? activePill : pill} onClick={() => setFilter(f)}>
                {f === 'all' ? 'All Orders' : f.replace(/([A-Z])/g, ' $1').trim()}
              </button>
            ))}
          </div>
          
          <div style={datePickerContainer}>
            <input type="date" style={dateInput} value={startDate} onChange={e => { setStartDate(e.target.value); setFilter('dateRange'); }} />
            <span style={{ color: '#94a3b8' }}>to</span>
            <input type="date" style={dateInput} value={endDate} onChange={e => { setEndDate(e.target.value); setFilter('dateRange'); }} />
          </div>
        </motion.div>

        {/* Digital Receipt Stack */}
        <div style={orderGrid}>
          <AnimatePresence mode='popLayout'>
            {filteredOrders.length === 0 ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={emptyState}>
                <FaReceipt size={50} color="#cbd5e1" />
                <h3 style={{ margin: '15px 0 5px', color: '#475569' }}>No orders found</h3>
                <p style={{ color: '#94a3b8' }}>Try adjusting your filters or date range.</p>
              </motion.div>
            ) : (
              filteredOrders.map((order, i) => (
                <motion.div 
                  key={order.id}
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: i * 0.05, type: 'spring' }}
                  style={ticketCard}
                  onClick={() => setSelectedOrder(order)}
                  whileHover={{ y: -5, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)' }}
                >
                  <div style={ticketLeft}>
                    <div style={ticketHeader}>
                      <span style={orderIdText}>{order.orderId}</span>
                      <div style={statusBadge(getStatusColor(order.orderStatus))}>
                        <motion.div animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }} transition={{ repeat: Infinity, duration: 2 }} style={statusDot(getStatusColor(order.orderStatus))} />
                        {order.orderStatus}
                      </div>
                    </div>
                    
                    <div style={ticketMeta}>
                      <span style={metaItem}><FaCalendarAlt /> {new Date(order.orderDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      <span style={metaItem}><FaCreditCard /> {order.paymentMode}</span>
                      <span style={metaItem}><FaBoxOpen /> {order.orderItems.length} Item(s)</span>
                    </div>
                  </div>

                  <div style={ticketRight}>
                    <div style={amountDisplay}>
                      <span style={amountLabel}>Total Amount</span>
                      <span style={amountValue}>₹{order.totalAmount.toFixed(2)}</span>
                    </div>
                    
                    <div style={actionRow}>
                      <button style={downloadBtn} onClick={(e) => generateInvoice(order, e)}>
                        <FaDownload /> PDF
                      </button>
                      <div style={arrowBtn}><FaChevronRight color="#94a3b8" /></div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Modern Glassmorphic Modal */}
      <AnimatePresence>
        {selectedOrder && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={modalOverlay} onClick={() => setSelectedOrder(null)}>
            <motion.div 
              initial={{ y: '100%', scale: 0.9 }} animate={{ y: 0, scale: 1 }} exit={{ y: '100%', scale: 0.9 }} 
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              style={modalContent} 
              onClick={(e) => e.stopPropagation()}
            >
              <div style={modalHeader}>
                <div>
                  <h2 style={modalTitle}>{selectedOrder.orderId}</h2>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>{new Date(selectedOrder.orderDate).toLocaleString()}</p>
                </div>
                <button onClick={() => setSelectedOrder(null)} style={closeBtn}><FaTimes size={20} /></button>
              </div>

              <div style={modalBody}>
                <div style={addressBlock}>
                  <h4 style={blockTitle}><FaMapMarkerAlt color="#3b82f6" /> Delivery Details</h4>
                  <p style={{ color: '#334155', lineHeight: '1.5', margin: 0 }}>{selectedOrder.fullAddress || 'N/A'}</p>
                </div>

                <div style={itemsList}>
                  <h4 style={blockTitle}>Purchased Items</h4>
                  {selectedOrder.orderItems.map((item, idx) => (
                    <div key={idx} style={itemRow}>
                      <div style={itemInfo}>
                        <div style={qtyBadge}>{item.quantity}x</div>
                        <span style={itemName}>{item.medicine.medicineName}</span>
                      </div>
                      <span style={itemPrice}>₹{item.price.toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <div style={receiptSummary}>
                  <div style={summaryRow}><span>Subtotal</span><span>₹{selectedOrder.subTotal.toFixed(2)}</span></div>
                  <div style={summaryRow}><span style={{ color: '#10b981' }}>Discount</span><span style={{ color: '#10b981' }}>-₹{selectedOrder.discount.toFixed(2)}</span></div>
                  <div style={summaryRow}><span>Fees</span><span>₹{selectedOrder.otherCharge.toFixed(2)}</span></div>
                  <div style={divider} />
                  <div style={grandTotalRow}><span>Total Paid via {selectedOrder.paymentMode}</span><span>₹{selectedOrder.totalAmount.toFixed(2)}</span></div>
                </div>
              </div>

              <div style={modalFooter}>
                <button style={primaryBtn} onClick={() => generateInvoice(selectedOrder)}><FaDownload style={{ marginRight: '8px' }}/> Download Full Invoice</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <Fotter />
    </div>
  );
};

// --- Next-Gen Inline Styles ---

const mainContainer = { maxWidth: '1000px', margin: '0 auto', padding: '120px 20px 60px' };
const headerSection = { marginBottom: '40px', textAlign: 'center' };
const title = { fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', margin: '0 0 10px', letterSpacing: '-0.02em' };
const subtitle = { fontSize: '1.1rem', color: '#64748b', margin: 0 };

const filterWrapper = { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '20px', background: '#fff', padding: '12px 16px', borderRadius: '100px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', marginBottom: '40px' };
const pillContainer = { display: 'flex', gap: '8px', overflowX: 'auto' };
const pill = { background: 'transparent', color: '#64748b', border: 'none', padding: '10px 20px', borderRadius: '50px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' };
const activePill = { ...pill, background: '#0f172a', color: '#fff', boxShadow: '0 4px 10px rgba(15, 23, 42, 0.2)' };
const datePickerContainer = { display: 'flex', alignItems: 'center', gap: '10px', paddingRight: '10px' };
const dateInput = { padding: '10px 15px', borderRadius: '50px', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', outline: 'none', fontFamily: 'inherit' };

const orderGrid = { display: 'flex', flexDirection: 'column', gap: '20px' };

const ticketCard = { background: '#fff', borderRadius: '24px', padding: '24px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '20px', cursor: 'pointer', border: '1px solid #f1f5f9', position: 'relative', overflow: 'hidden' };
const ticketLeft = { flex: '1 1 min-content' };
const ticketHeader = { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' };
const orderIdText = { fontSize: '1.1rem', fontWeight: 700, color: '#1e293b', fontFamily: 'monospace' };

const statusBadge = (color) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px', borderRadius: '50px', background: `${color}15`, color: color, fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' });
const statusDot = (color) => ({ width: '6px', height: '6px', borderRadius: '50%', background: color });

const ticketMeta = { display: 'flex', gap: '20px', flexWrap: 'wrap', color: '#64748b', fontSize: '0.9rem' };
const metaItem = { display: 'flex', alignItems: 'center', gap: '6px' };

const ticketRight = { display: 'flex', alignItems: 'center', gap: '30px', borderLeft: '1px dashed #e2e8f0', paddingLeft: '30px' };
const amountDisplay = { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' };
const amountLabel = { fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' };
const amountValue = { fontSize: '1.5rem', fontWeight: 800, color: '#0f172a' };
const actionRow = { display: 'flex', alignItems: 'center', gap: '15px' };
const downloadBtn = { display: 'flex', alignItems: 'center', gap: '6px', background: '#f1f5f9', color: '#3b82f6', border: 'none', padding: '10px 16px', borderRadius: '12px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' };
const arrowBtn = { background: '#f8fafc', width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' };

const emptyState = { textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: '24px', border: '1px dashed #cbd5e1' };
const loadingScreen = { height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f4f7f8' };
const spinner = { width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTop: '4px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' };

// Modal Styles
const modalOverlay = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-end' };
const modalContent = { background: '#fff', width: '100%', maxWidth: '600px', maxHeight: '90vh', borderRadius: '32px 32px 0 0', display: 'flex', flexDirection: 'column', boxShadow: '0 -20px 40px rgba(0,0,0,0.1)' };
if (typeof window !== 'undefined' && window.innerWidth > 600) { modalContent.borderRadius = '32px'; modalContent.alignSelf = 'center'; modalContent.height = 'auto'; }

const modalHeader = { padding: '30px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' };
const modalTitle = { fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', margin: '0 0 5px' };
const closeBtn = { background: '#f1f5f9', border: 'none', width: '40px', height: '40px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' };
const modalBody = { padding: '30px', overflowY: 'auto' };

const blockTitle = { fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', margin: '0 0 15px', fontWeight: 700 };
const addressBlock = { background: '#f8fafc', padding: '20px', borderRadius: '16px', marginBottom: '25px' };

const itemsList = { marginBottom: '30px' };
const itemRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 0', borderBottom: '1px solid #f1f5f9' };
const itemInfo = { display: 'flex', alignItems: 'center', gap: '15px' };
const qtyBadge = { background: '#eff6ff', color: '#3b82f6', padding: '4px 10px', borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem' };
const itemName = { fontWeight: 600, color: '#334155' };
const itemPrice = { fontWeight: 700, color: '#0f172a' };

const receiptSummary = { background: '#f8fafc', padding: '24px', borderRadius: '20px' };
const summaryRow = { display: 'flex', justifyContent: 'space-between', marginBottom: '12px', color: '#64748b', fontWeight: 500 };
const divider = { height: '1px', background: '#e2e8f0', margin: '15px 0' };
const grandTotalRow = { display: 'flex', justifyContent: 'space-between', fontSize: '1.2rem', fontWeight: 800, color: '#0f172a' };

const modalFooter = { padding: '20px 30px', borderTop: '1px solid #f1f5f9' };
const primaryBtn = { width: '100%', padding: '18px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: '16px', fontSize: '1.1rem', fontWeight: 700, cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center' };

export default UserOrders;